import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ScoresResponse, Team, TeamScore } from '@mv/contracts';
import Flag from './Flag';
import { api } from '../lib/api';

/**
 * The guest's own team, where it stands, and the way through to the Scorecard
 * (D56).
 *
 * This replaced a plain tag linking to `/scorecard`. The tag named the team and
 * nothing else, so the only reason to tap it was curiosity about a screen you
 * had to already know existed. A card that says *600 points, 2nd of 8* is worth
 * looking at on its own, and it is the same tap.
 *
 * **A failed fetch must not remove the way in.** The board is one request on the
 * app's landing screen, and Now is the page most likely to be opened on bad
 * hotel wifi. If it does not answer, the card still renders with the team and
 * still links — it simply says less. That is why there is no error state here
 * and no skeleton: the card is never absent, only quieter.
 *
 * It does not poll. The Scorecard itself does, and this is a card somebody
 * glances at on the way past.
 */
const ordinal = (n: number) => {
  // 11th–13th are the exceptions the naive rule gets wrong; with eight teams
  // they are unreachable, but the rule is written correctly rather than
  // written to the current arity.
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
};

export default function TeamStanding({ team }: { team: Team }) {
  const [standings, setStandings] = useState<TeamScore[] | null>(null);

  useEffect(() => {
    let live = true;
    api<ScoresResponse>('/scores')
      .then((res) => { if (live) setStandings(res.standings); })
      .catch(() => { /* Deliberately silent — see the note above. */ });
    return () => { live = false; };
  }, []);

  const mine = standings?.find((s) => s.team === team);
  const total = standings?.reduce((n, s) => n + s.points, 0) ?? 0;
  // Rank is only meaningful once somebody is ahead. While every team is on
  // nothing, "8th of 8" is an invented standing — the board itself makes the
  // same call about its rank column.
  const rank = standings && total > 0
    ? standings.findIndex((s) => s.team === team) + 1
    : null;

  return (
    <Link className="card team-standing" to="/scorecard">
      <Flag team={team} height={24} decorative />
      <span className="ts-main">
        <span className="ts-team">Team {team}</span>
        <span className="ts-sub">
          {rank ? `${ordinal(rank)} of ${standings!.length} · Scorecard` : 'Scorecard'}
        </span>
      </span>
      {mine && (
        <span className="ts-points">
          {mine.points.toLocaleString()}
          <span>{mine.points === 1 ? 'point' : 'points'}</span>
        </span>
      )}
      <span className="ts-chev" aria-hidden="true">&rsaquo;</span>
    </Link>
  );
}
